import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Leaf, Award, MapPin } from "lucide-react";

const items = [
  {
    icon: Sparkles,
    title: "Nurse-Led Care",
    body: "Private, unhurried consultations with Australia-trained cosmetic nurses.",
  },
  {
    icon: ShieldCheck,
    title: "Evidence-Based Protocols",
    body: "Treatments grounded in the latest peer-reviewed dermatological research.",
  },
  {
    icon: Leaf,
    title: "Elite Cosmeceuticals",
    body: "Curated formulations from leading clinical and naturopathic skincare houses.",
  },
  {
    icon: Award,
    title: "Refined Outcomes",
    body: "Subtle, age-appropriate results — never overdone, always recognisably you.",
  },
];

const CLINIC_IMG =
  "https://customer-assets.emergentagent.com/job_derma-studio-2/artifacts/ko6qfwhb_IMG_0515.jpeg";

export default function Difference() {
  return (
    <section
      id="difference"
      data-testid="difference-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5EFEB]"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 lg:sticky lg:top-32"
        >
          <p className="eyebrow mb-6">The Derma Difference</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-[#2D2926] mb-8">
            A boutique sanctuary
            <br />
            for <em className="italic text-[#C89F95]">whole-self</em> wellness.
          </h2>
          <p className="text-[#5C5753] leading-relaxed mb-8 max-w-md font-light">
            We're not a single-service clinic. Skin, weight management, women's health and hair are deeply
            interconnected — and treating them under one roof, with one nurse-led team, means
            your plan finally feels considered, not fragmented.
          </p>

          <div className="aspect-[4/5] overflow-hidden rounded-sm">
            <img
              src={CLINIC_IMG}
              alt="Modern clinic interior"
              data-testid="clinic-interior"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex items-center gap-2 mt-6 text-[#5C5753]">
            <MapPin size={14} strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.22em]">75 Daisy Hill Rd · QLD 4127</p>
          </div>
        </motion.div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#E8DFD8]">
          {items.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`difference-card-${i}`}
              className="group bg-[#FDFBF7] p-10 md:p-12 hover:bg-white transition-colors"
            >
              <item.icon
                size={28}
                strokeWidth={1}
                className="text-[#C89F95] mb-6 group-hover:text-[#B3867B] transition-colors"
              />
              <h3 className="font-serif text-2xl text-[#2D2926] mb-3 leading-tight">
                {item.title}
              </h3>
              <p className="text-sm text-[#5C5753] leading-relaxed font-light">
                {item.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
